import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Modal, Typography, generateUtilityClasses } from '@mui/material'
import { styled } from '@mui/system'
import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import DropdownFieldSearch from '../../../../ui/DropdownFieldSearch'
import { CustomCircular, TextField } from '../../../../ui'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import { useAddCreditCardTypeMutation, useEditCreditCardTypeMutation, useLazyCreditCardTypeDataQuery } from '../../../../services'
import { UspConfigurationCreditCardTypeViewResult } from '../../../../models/apiTypes'

const classes = generateUtilityClasses('Box', ['root', 'row', 'buttonWrap', 'saveBtn'])

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '390px',
    backgroundColor: theme.palette.background.main,
    boxShadow: 24,
    padding: theme.spacing(3),
  },
  [`& .${classes.row}`]: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  [`& .${classes.buttonWrap}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
    padding: theme.spacing(3, 0, 2, 0),
    marginTop: 'auto',
  },
  [`& .${classes.saveBtn}`]: {
    marginLeft: theme.spacing(2),
  },
}))

interface Props {
  rowData: UspConfigurationCreditCardTypeViewResult
  open: boolean
  onClose: () => void
  type: string
  submit: (...args: any) => any
}

const AddEditPaymentCreditCardTypeModal = ({ open, onClose, rowData, type, submit }: Props) => {
  const [initialValues, setInitialValues] = useState({})
  const [addCreditCardType, { isLoading: isLoadingCreditCardType, isSuccess: isSuccessCreditCardType }] = useAddCreditCardTypeMutation()
  const [editCreditCardType, { isLoading: isLoadingEditCreditCardType, isSuccess: isSuccessEditCreditCardType }] = useEditCreditCardTypeMutation()
  const [getCreditCardTypeData, { isLoading: isLoadingGetCreditCardType, isSuccess: isSuccessGetCreditCardType, data }] =
    useLazyCreditCardTypeDataQuery()

  const handleSubmit = (values: any) => {
    if (type === 'edit') {
      editCreditCardType({
        creditCardTypeId: rowData?.ID?.toString(),
        uspConfigurationCreditCardTypeAddEdit: {
          cardType: values.Description,
          cardShortCode: values.ShortCode,
          isAccepted: values.IsAccepted,
          isProcessInSystem: values.IsProcessedInSystem,
          isActive: values.IsActive,
        },
      })
    } else {
      addCreditCardType({
        uspConfigurationCreditCardTypeAddEdit: {
          cardType: values.Description,
          cardShortCode: values.ShortCode,
          isAccepted: values.IsAccepted,
          isProcessInSystem: values.IsProcessedInSystem,
          isActive: values.IsActive,
        },
      })
    }
  }

  useEffect(() => {
    if (open && rowData && type === 'edit')
      getCreditCardTypeData({
        creditCardTypeId: rowData?.ID?.toString() as string,
      })
  }, [open, type, rowData])

  useEffect(() => {
    if (type === 'add') {
      setInitialValues({
        Description: '',
        IsActive: true,
        IsProcessedInSystem: true,
        IsAccepted: true,
      })
    }
    if (isSuccessGetCreditCardType && type === 'edit') {
      setInitialValues({
        Description: data?.CardType,
        ShortCode: data?.CardShortCode,
        IsAccepted: data?.IsAccepted,
        IsProcessedInSystem: data?.IsProcessInSystem,
        IsActive: data?.IsActive,
      })
    }
  }, [isSuccessGetCreditCardType, data, type])

  useEffect(() => {
    if (isSuccessCreditCardType || isSuccessEditCreditCardType) {
      onClose()
      submit()
    }
  }, [isSuccessCreditCardType, isSuccessEditCreditCardType])

  const isLoading = useMemo(
    () => isLoadingCreditCardType || isLoadingEditCreditCardType || isLoadingGetCreditCardType,
    [isLoadingCreditCardType, isLoadingEditCreditCardType, isLoadingGetCreditCardType],
  )

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Credit Card Type</DialogTitle>
      <Form onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleSubmit }: any) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} py={1}>
                <Grid item xs={12}>
                  <TextField required name='Description' label='Description' fullWidth />
                </Grid>

                <Grid item xs={12}>
                  <TextField required name='ShortCode' label='Short Code' fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IsAccepted'
                    label='Is Accepted?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>

                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IsProcessedInSystem'
                    label='Is Processed In System?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>

                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IsActive'
                    label='Is Active?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <LoadingButton type='submit' className={classes.saveBtn} variant='contained'>
                Save
              </LoadingButton>
              <Button variant='contained' color='error' onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          </form>
        )}
      </Form>
      <CustomCircular open={isLoading} />
    </Dialog>
  )
}

export default AddEditPaymentCreditCardTypeModal
